import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import * as XLSX from "xlsx";

/**
 * CylInfoExcelManager
 * - Export cyl_info to .xlsx
 * - Import edited .xlsx, preview changes, apply back to Firestore
 * - Backup (duplicate) cyl_info into a new collection
 * - Restore backup back into cyl_info "untouched"
 *
 * Notes:
 * - This exports "flat" fields only. If you have nested objects/arrays they get JSON-stringified.
 * - Import expects at least an "id" column (docId). If missing, it falls back to "docId".
 * - Writes are chunked in batches of 500 to comply with Firestore limits.
 */

const EXPORT_LIMIT = 5000; // adjust if needed (pagination not included)
const BATCH_LIMIT = 500;

function pillClass(kind) {
  switch (kind) {
    case "ok":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "warn":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "bad":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}
function Pill({ kind, children }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        pillClass(kind),
      ].join(" ")}
    >
      {children}
    </span>
  );
}

function nowStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(
    d.getMinutes()
  )}${pad(d.getSeconds())}`;
}

function isPlainObject(v) {
  return v && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date);
}

function safeToCell(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  // Firestore Timestamp: try toDate
  if (typeof v?.toDate === "function") {
    try {
      const d = v.toDate();
      if (d instanceof Date) return d.toISOString();
    } catch {}
  }
  // arrays/objects -> JSON
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function tryParseCell(v) {
  // If it looks like JSON, parse it back. Otherwise keep as string/number/boolean.
  if (v === "" || v === null || v === undefined) return undefined; // treat blank as "leave alone" unless you want clearing
  if (typeof v !== "string") return v;

  const s = v.trim();
  if (!s) return undefined;

  // booleans
  if (s === "true") return true;
  if (s === "false") return false;

  // numbers (but avoid turning codes like "00123" into 123)
  if (/^-?\d+(\.\d+)?$/.test(s) && !/^0\d+/.test(s)) {
    const n = Number(s);
    if (Number.isFinite(n)) return n;
  }

  // json
  if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
    try {
      return JSON.parse(s);
    } catch {
      return v;
    }
  }

  return v;
}

function shallowDiff(oldObj, newObj) {
  // returns changed keys
  const changed = [];
  const allKeys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);
  for (const k of allKeys) {
    const a = oldObj?.[k];
    const b = newObj?.[k];

    const aStr = safeToCell(a);
    const bStr = safeToCell(b);

    if (aStr !== bStr) changed.push(k);
  }
  return changed;
}

async function commitInBatches({ collectionName, writes, mode }) {
  // writes: [{id, data}]
  // mode: "set" or "merge"
  let i = 0;
  while (i < writes.length) {
    const slice = writes.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(db);

    for (const w of slice) {
      const ref = doc(db, collectionName, w.id);
      if (mode === "set") batch.set(ref, w.data);
      else batch.set(ref, w.data, { merge: true });
    }

    await batch.commit();
    i += slice.length;
  }
}

export default function CylInfoExcelManager() {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [cylDocs, setCylDocs] = useState([]); // [{id, ...data}]
  const [loading, setLoading] = useState(false);

  // backup
  const [backupName, setBackupName] = useState(`cyl_info_backup_${nowStamp()}`);
  const [restoreName, setRestoreName] = useState("");

  // import
  const [importFile, setImportFile] = useState(null);
  const [importRows, setImportRows] = useState([]); // parsed rows from excel
  const [preview, setPreview] = useState(null); // {added, updated, unchanged, missingInImport, invalid}
  const [previewLimit] = useState(200); // show first N

  const cylById = useMemo(() => {
    const m = new Map();
    for (const d of cylDocs) m.set(d.id, d);
    return m;
  }, [cylDocs]);

  async function loadCylInfo() {
    setError("");
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "cyl_info"), orderBy("productCode", "asc"), limit(EXPORT_LIMIT))
      );
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setCylDocs(rows);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCylInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildExportRows(docs) {
    // Build union of keys (flat export)
    const keySet = new Set(["id", "productCode"]);
    for (const d of docs) {
      Object.keys(d || {}).forEach((k) => keySet.add(k));
    }
    const keys = Array.from(keySet);

    const out = docs.map((d) => {
      const row = {};
      for (const k of keys) row[k] = safeToCell(d?.[k]);
      return row;
    });

    return { keys, rows: out };
  }

  function exportToExcel() {
    setError("");
    try {
      const { rows } = buildExportRows(cylDocs);
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "cyl_info");
      XLSX.writeFile(wb, `cyl_info_export_${nowStamp()}.xlsx`);
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  async function parseExcel(file) {
    setError("");
    setPreview(null);
    setImportRows([]);
    if (!file) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" }); // keep empty cells

      // Normalize: require id/docId
      const cleaned = rows
        .map((r, idx) => {
          const id = String(r.id || r.docId || r.ID || "").trim();
          if (!id) return { __row: idx + 2, __invalid: true, reason: "Missing id/docId", raw: r };
          const obj = { id };
          for (const [k, v] of Object.entries(r)) {
            if (k === "id" || k === "docId" || k === "ID") continue;
            obj[k] = tryParseCell(v);
          }
          return obj;
        })
        .filter(Boolean);

      setImportRows(cleaned);
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  function buildPreview() {
    setError("");
    const invalid = importRows.filter((r) => r.__invalid);
    const valid = importRows.filter((r) => !r.__invalid);

    const added = [];
    const updated = [];
    const unchanged = [];
    const seenIds = new Set(valid.map((r) => r.id));

    for (const row of valid) {
      const existing = cylById.get(row.id);
      if (!existing) {
        added.push(row);
        continue;
      }

      // Only compare keys present in import row.
      // Blank cells become undefined -> we treat as "leave as-is"
      const mergedCandidate = { ...existing };
      for (const [k, v] of Object.entries(row)) {
        if (k === "id") continue;
        if (v === undefined) continue; // do not clear fields
        mergedCandidate[k] = v;
      }

      const changedKeys = shallowDiff(existing, mergedCandidate);
      if (changedKeys.length === 0) unchanged.push(row);
      else updated.push({ ...row, __changedKeys: changedKeys });
    }

    // If you want to detect docs existing in Firestore but NOT in import:
    const missingInImport = cylDocs
      .filter((d) => !seenIds.has(d.id))
      .map((d) => ({ id: d.id, productCode: d.productCode || "" }));

    setPreview({ invalid, added, updated, unchanged, missingInImport });
  }

  async function applyImport() {
    setError("");
    if (!preview) {
      setError("Build preview first.");
      return;
    }

    const invalid = preview.invalid || [];
    if (invalid.length > 0) {
      setError("Fix invalid rows (missing id) before applying.");
      return;
    }

    // Writes:
    // - added: set doc with row fields (and updatedAt)
    // - updated: merge with existing doc (only keys provided, blanks ignored), plus updatedAt
    const writes = [];

    for (const a of preview.added || []) {
      const data = { ...a };
      delete data.id;
      writes.push({
        id: a.id,
        data: { ...data, updatedAt: serverTimestamp() },
        mode: "set",
      });
    }

    for (const u of preview.updated || []) {
      const data = { ...u };
      delete data.id;
      delete data.__changedKeys;

      // strip undefined fields so we don't overwrite with undefined
      const cleaned = {};
      for (const [k, v] of Object.entries(data)) {
        if (v === undefined) continue;
        cleaned[k] = v;
      }

      writes.push({
        id: u.id,
        data: { ...cleaned, updatedAt: serverTimestamp() },
        mode: "merge",
      });
    }

    if (writes.length === 0) {
      setError("No changes to apply.");
      return;
    }

    setBusy(true);
    try {
      // Commit "set" writes then "merge" writes (separate to keep logic simple)
      const sets = writes.filter((w) => w.mode === "set").map((w) => ({ id: w.id, data: w.data }));
      const merges = writes.filter((w) => w.mode === "merge").map((w) => ({ id: w.id, data: w.data }));

      if (sets.length) await commitInBatches({ collectionName: "cyl_info", writes: sets, mode: "set" });
      if (merges.length) await commitInBatches({ collectionName: "cyl_info", writes: merges, mode: "merge" });

      // refresh
      await loadCylInfo();
      setImportFile(null);
      setImportRows([]);
      setPreview(null);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function backupCylInfo() {
    setError("");
    const name = String(backupName || "").trim();
    if (!name) {
      setError("Backup collection name required.");
      return;
    }

    setBusy(true);
    try {
      // Copy all cyl_info docs into backup collection with same doc IDs.
      // Adds metadata fields so you know it came from a backup.
      const writes = cylDocs.map((d) => {
        const data = { ...d };
        const id = data.id;
        delete data.id;
        return {
          id,
          data: {
            ...data,
            __backupFrom: "cyl_info",
            __backupAt: serverTimestamp(),
          },
        };
      });

      await commitInBatches({ collectionName: name, writes, mode: "set" });
      setRestoreName(name);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function restoreBackupIntoCylInfo() {
    setError("");
    const name = String(restoreName || "").trim();
    if (!name) {
      setError("Restore collection name required.");
      return;
    }

    // Safety: you are overwriting cyl_info docs with what's in backup.
    // This is what "untouched" means: identical clone.
    const ok = window.confirm(
      `Restore from "${name}" back into "cyl_info"?\n\nThis will overwrite documents in cyl_info that exist in the backup. Continue?`
    );
    if (!ok) return;

    setBusy(true);
    try {
      const snap = await getDocs(query(collection(db, name), limit(EXPORT_LIMIT)));
      const backupDocs = [];
      snap.forEach((d) => backupDocs.push({ id: d.id, ...d.data() }));

      if (backupDocs.length === 0) {
        setError(`No docs found in backup collection "${name}".`);
        return;
      }

      const writes = backupDocs.map((d) => {
        const data = { ...d };
        const id = data.id;
        delete data.id;

        // Remove backup metadata when restoring (optional).
        // If you truly want "untouched", comment these two deletes out.
        delete data.__backupFrom;
        delete data.__backupAt;

        return { id, data };
      });

      await commitInBatches({ collectionName: "cyl_info", writes, mode: "set" });
      await loadCylInfo();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  const stats = useMemo(() => {
    return {
      cyl: cylDocs.length,
      importRows: importRows.length,
      invalid: preview?.invalid?.length || 0,
      added: preview?.added?.length || 0,
      updated: preview?.updated?.length || 0,
      unchanged: preview?.unchanged?.length || 0,
      missing: preview?.missingInImport?.length || 0,
    };
  }, [cylDocs, importRows, preview]);

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">cyl_info Excel manager</h1>
          <p className="mt-1 text-sm text-slate-600">
            Export/import <span className="font-mono">cyl_info</span> via Excel, plus backup/restore.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Pill kind={loading ? "warn" : "ok"}>{loading ? "Loading…" : `cyl_info: ${stats.cyl}`}</Pill>
            <Pill kind={busy ? "warn" : "ok"}>{busy ? "Working…" : "Idle"}</Pill>
            {preview ? (
              <>
                <Pill kind={stats.invalid ? "bad" : "ok"}>Invalid: {stats.invalid}</Pill>
                <Pill kind="ok">Added: {stats.added}</Pill>
                <Pill kind={stats.updated ? "warn" : "ok"}>Updated: {stats.updated}</Pill>
                <Pill kind="ok">Unchanged: {stats.unchanged}</Pill>
                <Pill kind={stats.missing ? "warn" : "ok"}>Missing in import: {stats.missing}</Pill>
              </>
            ) : null}
          </div>

          {error ? (
            <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            disabled={loading || busy}
            onClick={loadCylInfo}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Refresh
          </button>
          <button
            disabled={loading || busy || cylDocs.length === 0}
            onClick={exportToExcel}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            Export to Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* IMPORT */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold">Import edited Excel</div>
          <div className="mt-1 text-sm text-slate-600">
            Expected columns: <span className="font-mono">id</span> (docId), plus any fields to update.
            <br />
            Blank cells are treated as “leave as-is” (not clearing fields).
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={async (e) => {
                const f = e.target.files?.[0] || null;
                setImportFile(f);
                await parseExcel(f);
              }}
              className="text-sm"
              disabled={busy}
            />

            <button
              disabled={busy || importRows.length === 0}
              onClick={buildPreview}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Build preview
            </button>

            <button
              disabled={busy || !preview || (preview?.invalid?.length || 0) > 0}
              onClick={applyImport}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              Apply changes
            </button>
          </div>

          {preview ? (
            <div className="mt-4 space-y-3">
              {(preview.invalid?.length || 0) > 0 ? (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                  {preview.invalid.length} invalid rows (missing id). Fix them before applying.
                </div>
              ) : null}

              <div className="rounded-lg border border-slate-200">
                <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
                  Updated (showing first {previewLimit})
                </div>
                <div className="max-h-48 overflow-auto">
                  {preview.updated.length === 0 ? (
                    <div className="p-3 text-sm text-slate-500">No updates.</div>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead className="sticky top-0 bg-white text-slate-600">
                        <tr className="border-b border-slate-100">
                          <th className="px-3 py-2 font-medium">id</th>
                          <th className="px-3 py-2 font-medium">changed keys</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {preview.updated.slice(0, previewLimit).map((r) => (
                          <tr key={r.id} className="hover:bg-slate-50/60">
                            <td className="px-3 py-2 font-mono">{r.id}</td>
                            <td className="px-3 py-2 text-slate-700">
                              <span className="font-mono text-xs">{(r.__changedKeys || []).join(", ")}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200">
                <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
                  Added (showing first {previewLimit})
                </div>
                <div className="max-h-40 overflow-auto">
                  {preview.added.length === 0 ? (
                    <div className="p-3 text-sm text-slate-500">No new docs.</div>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead className="sticky top-0 bg-white text-slate-600">
                        <tr className="border-b border-slate-100">
                          <th className="px-3 py-2 font-medium">id</th>
                          <th className="px-3 py-2 font-medium">productCode</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {preview.added.slice(0, previewLimit).map((r) => (
                          <tr key={r.id} className="hover:bg-slate-50/60">
                            <td className="px-3 py-2 font-mono">{r.id}</td>
                            <td className="px-3 py-2 font-mono">{String(r.productCode || "")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200">
                <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
                  Missing in import (docs in Firestore not present in your Excel) (first {previewLimit})
                </div>
                <div className="max-h-40 overflow-auto">
                  {preview.missingInImport.length === 0 ? (
                    <div className="p-3 text-sm text-slate-500">None.</div>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead className="sticky top-0 bg-white text-slate-600">
                        <tr className="border-b border-slate-100">
                          <th className="px-3 py-2 font-medium">id</th>
                          <th className="px-3 py-2 font-medium">productCode</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {preview.missingInImport.slice(0, previewLimit).map((r) => (
                          <tr key={r.id} className="hover:bg-slate-50/60">
                            <td className="px-3 py-2 font-mono">{r.id}</td>
                            <td className="px-3 py-2 font-mono">{r.productCode || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-3 text-sm text-slate-500">
              Upload your edited Excel, then click <span className="font-semibold">Build preview</span>.
            </div>
          )}
        </div>

        {/* BACKUP / RESTORE */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold">Backup / duplicate cyl_info</div>
          <div className="mt-1 text-sm text-slate-600">
            “Duplicate” means: copy every doc from <span className="font-mono">cyl_info</span> into another collection,
            same doc IDs, same fields.
          </div>

          <div className="mt-3 grid gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Backup collection name</label>
              <input
                value={backupName}
                onChange={(e) => setBackupName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                disabled={busy}
              />
              <div className="mt-1 text-xs text-slate-500">
                Example: <span className="font-mono">cyl_info_backup_20260305_121500</span>
              </div>
            </div>

            <button
              disabled={busy || loading || cylDocs.length === 0}
              onClick={backupCylInfo}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              Create backup (duplicate)
            </button>

            <div className="border-t border-slate-200 pt-3">
              <div className="text-sm font-semibold">Restore backup into cyl_info</div>
              <div className="mt-1 text-sm text-slate-600">
                This overwrites docs in <span className="font-mono">cyl_info</span> that exist in the backup collection.
              </div>

              <label className="mt-3 block text-xs font-medium text-slate-600">Restore from collection</label>
              <input
                value={restoreName}
                onChange={(e) => setRestoreName(e.target.value)}
                placeholder='e.g. cyl_info_backup_20260305_121500'
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                disabled={busy}
              />

              <button
                disabled={busy || !restoreName}
                onClick={restoreBackupIntoCylInfo}
                className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
              >
                Restore backup → cyl_info (overwrite)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* QUICK VIEW */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">Quick sense check</div>
            <div className="text-sm text-slate-600">First 200 docs by productCode.</div>
          </div>
          <Pill kind="ok">Loaded: {cylDocs.length}</Pill>
        </div>

        <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-50 text-slate-600">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2 font-medium">id</th>
                  <th className="px-3 py-2 font-medium">productCode</th>
                  <th className="px-3 py-2 font-medium">description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cylDocs.slice(0, 200).map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-mono">{d.id}</td>
                    <td className="px-3 py-2 font-mono">{String(d.productCode || "")}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {String(d.productDescription || d.description || "")}
                    </td>
                  </tr>
                ))}
                {cylDocs.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-slate-500">
                      No cyl_info docs found (or you hit the export limit).
                    </td>
                  </tr>
                ) : null}
                {loading ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-slate-500">
                      Loading…
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-2 text-xs text-slate-500">
          Export limit currently set to <span className="font-mono">{EXPORT_LIMIT}</span>. If you have more, we’ll add pagination.
        </div>
      </div>
    </div>
  );
}